<template>
  <AppLayout>
    <div class="flex justify-between items-center mb-4 mt-4">
      <h1>External Products</h1>

      <!-- Bulk Actions Bar -->
      <div v-if="hasSelection" class="flex items-center gap-3">
        <span class="text-surface-600">
          <template v-if="selectAllFiltered">
            All {{ totalRecords }} filtered products selected
          </template>
          <template v-else> {{ selectedProductIds.size }} selected </template>
        </span>
        <Button
          label="Metadata Image Size"
          icon="pi pi-image"
          severity="primary"
          size="small"
          :loading="bulkActionLoading"
          @click="executeBulkAction('metadataImageSize')"
        />
        <Button
          label="Download"
          icon="pi pi-cloud-download"
          severity="primary"
          size="small"
          :loading="bulkActionLoading"
          @click="executeBulkAction('download')"
        />
        <Button
          label="Process"
          icon="pi pi-play"
          severity="primary"
          size="small"
          :loading="bulkActionLoading"
          @click="executeBulkAction('process')"
        />
        <Button
          label="Upload"
          icon="pi pi-cloud-upload"
          severity="primary"
          size="small"
          :loading="bulkActionLoading"
          @click="executeBulkAction('upload')"
        />
        <Button
          label="Upload New Images"
          icon="pi pi-cloud-upload"
          severity="primary"
          size="small"
          :loading="bulkActionLoading"
          @click="executeBulkAction('uploadNewImages')"
        />
        <Button
          label="Create Lifestyle"
          icon="pi pi-sparkles"
          severity="secondary"
          size="small"
          :loading="bulkActionLoading"
          @click="executeBulkAction('createLifestyle')"
        />
        <Button
          icon="pi pi-times"
          severity="secondary"
          text
          size="small"
          @click="clearSelection"
          v-tooltip.top="'Clear selection'"
        />
      </div>
    </div>

    <div class="mb-4 flex gap-4 items-center">
      <IconField>
        <InputIcon class="pi pi-search" />
        <InputText v-model="searchQuery" placeholder="Search by name or tag..." class="w-80" />
      </IconField>
      <Select
        v-model="selectedTag"
        :options="availableTags"
        placeholder="All Tags"
        class="w-48"
        showClear
        @change="onTagChange"
      />
      <Select
        v-model="selectedFilter"
        :options="filterOptions"
        optionLabel="label"
        optionValue="value"
        placeholder="Filter"
        class="w-56"
        @change="onFilterChange"
      />
    </div>

    <DataTable
      :value="products"
      :loading="loading"
      paginator
      :rows="50"
      :rowsPerPageOptions="[50, 100, 200]"
      :totalRecords="totalRecords"
      :lazy="true"
      @page="onPage"
      tableStyle="min-width: 50rem"
      class="hoverable-rows"
      @row-click="onRowClick"
    >
      <!-- Checkbox column -->
      <Column header="" style="width: 50px">
        <template #header>
          <div class="flex flex-col items-center gap-1">
            <Checkbox
              :modelValue="isAllPageSelected"
              :indeterminate="isSomePageSelected && !isAllPageSelected"
              :binary="true"
              @change="togglePageSelection"
              @click.stop
            />
            <button
              v-if="hasSelection && !selectAllFiltered"
              class="text-xs text-primary-600 hover:text-primary-700 whitespace-nowrap bg-transparent border-none cursor-pointer p-0"
              @click.stop="toggleSelectAllFiltered"
            >
              Select all {{ totalRecords }}
            </button>
          </div>
        </template>
        <template #body="{ data }">
          <Checkbox
            :modelValue="isSelected(data.id)"
            :binary="true"
            @change="() => toggleSelection(data.id)"
            @click.stop
          />
        </template>
      </Column>
      <Column header="" style="width: 110px">
        <template #body="{ data }">
          <div class="flex gap-1">
            <!-- Original image -->
            <img
              v-if="data.firstImageUrl"
              :src="data.firstImageUrl"
              :alt="data.name"
              class="w-10 h-10 object-cover rounded"
              loading="lazy"
            />
            <div v-else class="w-10 h-10 bg-surface-100 rounded flex items-center justify-center">
              <i class="pi pi-image text-surface-400"></i>
            </div>
            <!-- Processed image -->
            <img
              v-if="data.firstProcessedImageUrl"
              :src="data.firstProcessedImageUrl"
              :alt="`${data.name} (processed)`"
              class="w-10 h-10 object-cover rounded ring-2 ring-purple-400"
              loading="lazy"
            />
            <div v-else class="w-10 h-10 bg-surface-100 rounded flex items-center justify-center">
              <i class="pi pi-sparkles text-surface-400"></i>
            </div>
          </div>
        </template>
      </Column>
      <Column field="name" header="Name">
        <template #body="{ data }">
          {{ data.name }}<br />
          <small>{{ data.externalProductGuid }}</small>
        </template>
      </Column>
      <Column field="source" header="Source">
        <template #body="{ data }">
          <Tag :value="data.source" severity="info" />
        </template>
      </Column>
      <Column field="metadata" header="Metadata">
        <template #body="{ data }">
          <div v-if="data.metadata" class="flex flex-col gap-1 text-sm">
            <div v-if="data.metadata.stockAmount !== undefined" class="text-surface-700">
              <span class="text-surface-500">Stock:</span> {{ data.metadata.stockAmount }}
            </div>
            <div v-if="data.metadata.defaultCategoryName" class="text-surface-700">
              <span class="text-surface-500">Category:</span>
              <button
                @click.stop="filterByCategory(data.metadata.defaultCategoryName)"
                class="text-primary-600 hover:text-primary-700 hover:underline cursor-pointer bg-transparent border-none p-0 font-normal"
              >
                {{ data.metadata.defaultCategoryName }}
              </button>
            </div>
            <span
              v-if="!data.metadata.stockAmount && !data.metadata.defaultCategoryName"
              class="text-surface-400"
              >—</span
            >
          </div>
          <span v-else class="text-surface-400">—</span>
        </template>
      </Column>
      <Column field="productId" header="In Phottr" style="width: 100px">
        <template #body="{ data }">
          <Tag v-if="data.productId" severity="success" class="gap-1">
            <i class="pi pi-check text-xs"></i>
            <span>Linked</span>
          </Tag>
          <span v-else class="text-surface-400">—</span>
        </template>
      </Column>
      <Column field="tags" header="Tags">
        <template #body="{ data }">
          <div class="flex flex-wrap gap-1">
            <Tag v-for="tag in data.tags" :key="tag" :value="tag" severity="secondary" />
          </div>
        </template>
      </Column>
      <Column field="created" header="Created">
        <template #body="{ data }">
          {{ formatDate(data.created) }}
        </template>
      </Column>
    </DataTable>

    <!-- External Product Drawer -->
    <Drawer
      v-model:visible="drawerVisible"
      position="right"
      :class="['external-product-drawer', { 'drawer-with-phottr': phottrDrawerVisible }]"
      :modal="!phottrDrawerVisible"
      @hide="onExternalDrawerHide"
    >
      <template #header>
        <h2 class="text-xl font-semibold">External Product</h2>
      </template>

      <div v-if="loadingDetail" class="flex justify-center p-8">
        <ProgressSpinner style="width: 50px; height: 50px" />
      </div>

      <div v-else-if="selectedProduct" class="flex flex-col gap-4">
        <div>
          <label class="text-surface-500 text-sm">Name</label>
          <div class="text-lg font-medium">{{ selectedProduct.name }}</div>
        </div>

        <div>
          <label class="text-surface-500 text-sm">Source</label>
          <div>
            <Tag :value="selectedProduct.source" severity="info" />
          </div>
        </div>

        <div>
          <label class="text-surface-500 text-sm">External ID</label>
          <div class="font-mono text-sm">{{ selectedProduct.externalProductGuid }}</div>
        </div>

        <div
          v-if="selectedProduct.productId"
          class="bg-green-50 border border-green-200 rounded-lg p-3"
        >
          <div class="flex items-center gap-2 mb-2">
            <i class="pi pi-check-circle text-green-600"></i>
            <span class="text-green-700 font-medium">Linked to Phottr</span>
          </div>
          <button
            @click="openPhottrProduct(selectedProduct.productId!)"
            class="inline-flex items-center gap-1 text-primary-600 hover:text-primary-700 font-medium text-sm cursor-pointer bg-transparent border-none p-0"
          >
            <i class="pi pi-images text-xs"></i>
            View photos in Phottr
          </button>
        </div>

        <div v-if="selectedProduct.tags && selectedProduct.tags.length > 0">
          <label class="text-surface-500 text-sm">Tags</label>
          <div class="flex flex-wrap gap-1 mt-1">
            <Tag v-for="tag in selectedProduct.tags" :key="tag" :value="tag" severity="secondary" />
          </div>
        </div>

        <div>
          <label class="text-surface-500 text-sm">Created</label>
          <div>{{ formatDate(selectedProduct.created) }}</div>
        </div>

        <Divider />

        <div>
          <label class="text-surface-500 text-sm mb-2 block"
            >Images ({{ selectedProduct.images?.length ?? 0 }})</label
          >
          <div
            v-if="selectedProduct.images && selectedProduct.images.length > 0"
            class="grid grid-cols-2 gap-3"
          >
            <div
              v-for="image in selectedProduct.images"
              :key="image.id"
              class="border border-surface-200 rounded-lg overflow-hidden"
            >
              <div class="relative">
                <img
                  :src="image.url"
                  :alt="image.fileName"
                  class="w-full h-40 object-cover"
                  loading="lazy"
                />
                <div
                  v-if="image.status?.downloadedAt"
                  class="absolute top-2 right-2 bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center shadow-md"
                  v-tooltip.top="'Imported to Phottr'"
                >
                  <i class="pi pi-cloud-download text-xs"></i>
                </div>
                <div
                  v-if="image.status?.processedAt"
                  class="absolute top-10 right-2 bg-purple-500 text-white rounded-full w-6 h-6 flex items-center justify-center shadow-md"
                  v-tooltip.top="'Processed'"
                >
                  <i class="pi pi-sparkles text-xs"></i>
                </div>
                <div
                  v-if="image.status?.uploadedAt"
                  class="absolute top-18 right-2 bg-green-500 text-white rounded-full w-6 h-6 flex items-center justify-center shadow-md"
                  v-tooltip.top="'Uploaded'"
                >
                  <i class="pi pi-cloud-upload text-xs"></i>
                </div>
              </div>
              <div class="p-2 flex flex-col gap-2">
                <div class="flex items-center justify-between gap-2">
                  <span class="text-xs text-surface-500 truncate">{{ image.fileName }}</span>
                  <!--
                    v-if="!image.imageId" -->
                  <!-- <Button
                    icon="pi pi-cloud-download"
                    size="small"
                    severity="secondary"
                    :loading="processingImageId === image.id"
                    @click.stop="processImage(image.id)"
                    v-tooltip.top="'Import to Phottr'"
                  /> 
                  
                    v-if="!image.uploaded"-->
                  <Button
                    icon="pi pi-cloud-upload"
                    size="small"
                    severity="secondary"
                    :loading="processingImageId === image.id"
                    @click.stop="uploadImage(image.id)"
                    v-tooltip.top="'Upload to Shoptet'"
                  />
                </div>
              </div>
            </div>
          </div>
          <div v-else class="text-surface-400 italic">No images available</div>
        </div>
      </div>
    </Drawer>

    <!-- Phottr Product Drawer -->
    <Drawer
      v-model:visible="phottrDrawerVisible"
      position="right"
      class="phottr-product-drawer"
      :modal="false"
    >
      <template #header>
        <h2 class="text-xl font-semibold">Phottr Product</h2>
      </template>

      <div v-if="loadingPhottrProduct" class="flex justify-center p-8">
        <ProgressSpinner style="width: 50px; height: 50px" />
      </div>

      <div v-else-if="phottrProduct" class="flex flex-col gap-4">
        <div>
          <label class="text-surface-500 text-sm">Name</label>
          <div class="text-lg font-medium">{{ phottrProduct.name }}</div>
        </div>

        <div>
          <label class="text-surface-500 text-sm">Code</label>
          <div class="font-mono text-sm">{{ phottrProduct.code }}</div>
        </div>

        <div>
          <label class="text-surface-500 text-sm">Created</label>
          <div>{{ formatDate(phottrProduct.created) }}</div>
        </div>

        <Divider />

        <div>
          <label class="text-surface-500 text-sm mb-2 block"
            >Photos ({{ phottrImages.length }})</label
          >
          <div v-if="phottrImages.length > 0" class="flex flex-col gap-4">
            <div
              v-for="image in phottrImages"
              :key="image.id"
              class="border border-surface-200 rounded-lg overflow-hidden"
            >
              <!-- Main image preview (clickable if multiple versions) -->
              <div
                class="relative"
                :class="{
                  'cursor-pointer hover:opacity-90 transition-opacity': image.versions.length > 1,
                }"
                @click="image.versions.length > 1 && toggleExpandedImage(image.id)"
              >
                <img
                  :src="getImagePreviewUrl(image)"
                  :alt="`Image ${image.order}`"
                  class="w-full h-48 object-contain bg-surface-50"
                  loading="lazy"
                />
                <div
                  v-if="image.attention"
                  class="absolute top-2 right-2 bg-orange-500 text-white rounded-full w-6 h-6 flex items-center justify-center shadow-md"
                  v-tooltip.top="image.attentionText || 'Needs attention'"
                >
                  <i class="pi pi-exclamation-triangle text-xs"></i>
                </div>
                <!-- Expand indicator -->
                <div
                  v-if="image.versions.length > 1"
                  class="absolute bottom-2 right-2 bg-black/60 text-white rounded px-2 py-1 text-xs flex items-center gap-1"
                >
                  <i
                    class="pi transition-transform"
                    :class="expandedImageId === image.id ? 'pi-chevron-up' : 'pi-chevron-down'"
                  ></i>
                  {{ image.versions.length }} versions
                </div>
              </div>

              <!-- Expanded versions row -->
              <div
                v-if="expandedImageId === image.id && image.versions.length > 1"
                class="bg-surface-100 border-t border-surface-200"
              >
                <div class="p-2 text-xs text-surface-500 font-medium">All versions:</div>
                <div class="versions-scroll flex gap-2 px-2 pb-2 overflow-x-auto">
                  <div
                    v-for="version in image.versions"
                    :key="version.id"
                    class="flex-shrink-0 border rounded-lg overflow-hidden bg-white"
                    :class="{
                      'ring-2 ring-primary-500': version.id === image.selectedVersionId,
                      'ring-2 ring-green-500':
                        version.original && version.id !== image.selectedVersionId,
                    }"
                  >
                    <img
                      :src="version.url ?? ''"
                      :alt="version.original ? 'Original' : 'Processed'"
                      class="w-24 h-24 object-contain bg-surface-50"
                      loading="lazy"
                    />
                    <div class="p-1 text-center">
                      <span class="text-xs text-surface-600">
                        {{ version.original ? 'Original' : 'Processed' }}
                      </span>
                      <div
                        v-if="version.id === image.selectedVersionId"
                        class="text-xs text-primary-600 font-medium"
                      >
                        Selected
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="p-2">
                <div class="flex items-center justify-between gap-2">
                  <span class="text-xs text-surface-500"
                    >Order: {{ image.order }} · {{ image.versions.length }} version(s)</span
                  >
                </div>
                <div v-if="image.attention && image.attentionText" class="mt-1">
                  <span class="text-xs text-orange-600">{{ image.attentionText }}</span>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="text-surface-400 italic">No photos available</div>
        </div>

        <Divider />

        <router-link
          :to="`/products/${phottrProduct.id}`"
          class="inline-flex items-center justify-center gap-2 bg-primary-500 hover:bg-primary-600 text-white font-medium py-2 px-4 rounded-lg transition-colors"
        >
          <i class="pi pi-external-link text-sm"></i>
          Open full product page
        </router-link>
      </div>
    </Drawer>
  </AppLayout>
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue';
import { useDebounceFn } from '@vueuse/core';
import dayjs from 'dayjs';
import AppLayout from '@/components/AppLayout.vue';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import InputText from 'primevue/inputtext';
import IconField from 'primevue/iconfield';
import InputIcon from 'primevue/inputicon';
import Tag from 'primevue/tag';
import Drawer from 'primevue/drawer';
import Divider from 'primevue/divider';
import ProgressSpinner from 'primevue/progressspinner';
import Button from 'primevue/button';
import Select from 'primevue/select';
import Checkbox from 'primevue/checkbox';
import { useApiClient } from '@/api/apiClient';
import { useToast } from 'primevue/usetoast';
import type {
  ExternalProductDto,
  ExternalProductWithImagesDto,
  ProductDto,
  ImageDto,
} from '@/api/generated';

// Bulk action types (matches backend enum)
type ExternalProductBulkActionType =
  | 'download'
  | 'metadataImageSize'
  | 'process'
  | 'upload'
  | 'uploadNewImages'
  | 'createLifestyle';

const { api } = useApiClient();
const toast = useToast();

const products = ref<ExternalProductDto[]>([]);
const loading = ref(false);
const totalRecords = ref(0);
const currentOffset = ref(0);
const currentLimit = ref(50);
const searchQuery = ref('');
const selectedFilter = ref<string>('all');
const selectedTag = ref<string | null>(null);
const availableTags = ref<string[]>([]);

const filterOptions = [
  { label: 'All Products', value: 'all' },
  { label: 'Stuck Operations (>30min)', value: 'stuckOperations' },
  { label: 'Has Operations', value: 'hasOperations' },
  { label: 'Pending Operations', value: 'pendingOperations' },
];

// Selection state
const selectedProductIds = ref<Set<string>>(new Set());
const selectAllFiltered = ref(false);
const bulkActionLoading = ref(false);

// Selection computed properties
const hasSelection = computed(() => selectedProductIds.value.size > 0 || selectAllFiltered.value);

const isAllPageSelected = computed(() => {
  if (products.value.length === 0) return false;
  return products.value.every(p => selectedProductIds.value.has(p.id));
});

const isSomePageSelected = computed(() => {
  return products.value.some(p => selectedProductIds.value.has(p.id));
});

// Selection methods
function isSelected(id: string): boolean {
  return selectAllFiltered.value || selectedProductIds.value.has(id);
}

function toggleSelection(id: string) {
  if (selectAllFiltered.value) {
    // When "select all filtered" is active, clicking a checkbox deselects it
    // Convert to explicit selection of all except this one
    selectAllFiltered.value = false;
    // Add all current page items except the clicked one
    products.value.forEach(p => {
      if (p.id !== id) {
        selectedProductIds.value.add(p.id);
      }
    });
  } else {
    if (selectedProductIds.value.has(id)) {
      selectedProductIds.value.delete(id);
    } else {
      selectedProductIds.value.add(id);
    }
  }
  // Trigger reactivity
  selectedProductIds.value = new Set(selectedProductIds.value);
}

function togglePageSelection() {
  if (isAllPageSelected.value) {
    // Deselect all on current page
    products.value.forEach(p => selectedProductIds.value.delete(p.id));
    selectAllFiltered.value = false;
  } else {
    // Select all on current page
    products.value.forEach(p => selectedProductIds.value.add(p.id));
  }
  // Trigger reactivity
  selectedProductIds.value = new Set(selectedProductIds.value);
}

function toggleSelectAllFiltered() {
  selectAllFiltered.value = !selectAllFiltered.value;
  if (selectAllFiltered.value) {
    // Clear individual selections when selecting all filtered
    selectedProductIds.value.clear();
  }
}

function clearSelection() {
  selectedProductIds.value.clear();
  selectAllFiltered.value = false;
  // Trigger reactivity
  selectedProductIds.value = new Set(selectedProductIds.value);
}

async function executeBulkAction(action: ExternalProductBulkActionType) {
  try {
    bulkActionLoading.value = true;

    const requestBody = selectAllFiltered.value
      ? {
          action,
          selectAll: true,
          search: searchQuery.value || undefined,
          filter: selectedFilter.value || undefined,
          tag: selectedTag.value || undefined,
        }
      : {
          action,
          ids: Array.from(selectedProductIds.value),
        };

    // Use fetch directly until API client is regenerated
    const response = await fetch('/api/external-products/bulk-action', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      throw new Error('Bulk action failed');
    }

    const result = await response.json();
    toast.add({
      severity: result?.failed ? 'warn' : 'success',
      summary: 'Bulk Action Completed',
      detail: `Processed: ${result?.processed}, Failed: ${result?.failed}`,
      life: 5000,
    });

    clearSelection();
    await loadProducts();
  } catch (err) {
    console.error('Bulk action failed', err);
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Bulk action failed',
      life: 3000,
    });
  } finally {
    bulkActionLoading.value = false;
  }
}

const drawerVisible = ref(false);
const selectedProduct = ref<ExternalProductWithImagesDto | null>(null);
const loadingDetail = ref(false);
const processingImageId = ref<string | null>(null);

// Phottr product drawer state
const phottrDrawerVisible = ref(false);
const phottrProduct = ref<ProductDto | null>(null);
const phottrImages = ref<ImageDto[]>([]);
const loadingPhottrProduct = ref(false);
const expandedImageId = ref<string | null>(null);

async function loadProducts() {
  try {
    loading.value = true;
    const { data, error } = await api.externalProductControllerList({
      query: {
        search: searchQuery.value,
        offset: String(currentOffset.value),
        limit: String(currentLimit.value),
        filter: selectedFilter.value,
        tag: selectedTag.value ?? '',
      },
    });
    if (error) {
      throw new Error('Failed to load external products');
    }
    products.value = data?.data ?? [];
    totalRecords.value = data?.total ?? 0;
  } catch (err) {
    console.error('Failed to load external products', err);
  } finally {
    loading.value = false;
  }
}

async function loadTags() {
  try {
    const { data, error } = await api.externalProductControllerGetTags();
    if (error) {
      console.error('Failed to load tags', error);
      return;
    }
    availableTags.value = data ?? [];
  } catch (err) {
    console.error('Failed to load tags', err);
  }
}

function onFilterChange() {
  currentOffset.value = 0;
  clearSelection();
  loadProducts();
}

function onTagChange() {
  currentOffset.value = 0;
  clearSelection();
  loadProducts();
}

function filterByCategory(categoryName: string) {
  searchQuery.value = categoryName;
  currentOffset.value = 0;
  clearSelection();
  loadProducts();
}

const debouncedSearch = useDebounceFn(() => {
  currentOffset.value = 0;
  clearSelection();
  loadProducts();
}, 300);

watch(searchQuery, () => {
  debouncedSearch();
});

function onPage(event: { first: number; rows: number }) {
  currentOffset.value = event.first;
  currentLimit.value = event.rows;
  loadProducts();
}

async function onRowClick(event: { data: ExternalProductDto }) {
  drawerVisible.value = true;
  loadingDetail.value = true;
  selectedProduct.value = null;

  try {
    const { data, error } = await api.externalProductControllerGet({
      path: { id: event.data.id },
    });
    if (error) {
      throw new Error('Failed to load product details');
    }
    selectedProduct.value = data ?? null;
  } catch (err) {
    console.error('Failed to load product details', err);
  } finally {
    loadingDetail.value = false;
  }
}

async function _processImage(imageId: string) {
  try {
    processingImageId.value = imageId;
    const { data, error } = await api.externalProductControllerProcessImage({
      path: { id: imageId },
    });
    if (error) {
      throw new Error('Failed to process image');
    }
    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: 'Image imported successfully',
      life: 3000,
    });
    // Update the image in selectedProduct to show it's been imported
    if (selectedProduct.value?.images) {
      const image = selectedProduct.value.images.find(img => img.id === imageId);
      if (image) {
        image.imageId = data?.imageId;
      }
    }
    // Update productId if the product is now linked
    if (selectedProduct.value && data?.productId) {
      selectedProduct.value.productId = data.productId;
      // Also update the product in the list
      const product = products.value.find(p => p.id === selectedProduct.value?.id);
      if (product) {
        product.productId = data.productId;
      }
    }
  } catch (err) {
    console.error('Failed to process image', err);
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to import image',
      life: 3000,
    });
  } finally {
    processingImageId.value = null;
  }
}

async function openPhottrProduct(productId: string) {
  phottrDrawerVisible.value = true;
  loadingPhottrProduct.value = true;
  phottrProduct.value = null;
  phottrImages.value = [];
  expandedImageId.value = null;

  try {
    const [productResponse, imagesResponse] = await Promise.all([
      api.productControllerGet({ path: { id: productId } }),
      api.productControllerGetImages({ path: { productId } }),
    ]);

    if (productResponse.error) {
      throw new Error('Failed to load Phottr product');
    }
    if (imagesResponse.error) {
      throw new Error('Failed to load Phottr images');
    }

    phottrProduct.value = productResponse.data ?? null;
    phottrImages.value = imagesResponse.data ?? [];
  } catch (err) {
    console.error('Failed to load Phottr product', err);
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to load Phottr product',
      life: 3000,
    });
  } finally {
    loadingPhottrProduct.value = false;
  }
}

function getImagePreviewUrl(image: ImageDto): string {
  // Find selected version, or the original, or the first available version
  const selectedVersion = image.versions.find(v => v.id === image.selectedVersionId);
  const originalVersion = image.versions.find(v => v.original);
  const version = selectedVersion ?? originalVersion ?? image.versions[0];
  return version?.url ?? '';
}

function toggleExpandedImage(imageId: string) {
  expandedImageId.value = expandedImageId.value === imageId ? null : imageId;
}

function onExternalDrawerHide() {
  // Close the Phottr drawer when external drawer is closed
  phottrDrawerVisible.value = false;
}

function formatDate(timestamp: number | undefined): string {
  if (!timestamp) return '-';
  return dayjs(timestamp).format('MMM D, YYYY HH:mm');
}

async function uploadImage(imageId: string) {
  try {
    await api.shoptetExternalProductControllerUploadExternalProductImage({
      path: { id: imageId },
    });
    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: 'Image uploaded to Shoptet',
      life: 3000,
    });
  } catch (err) {
    console.error('Failed to upload image', err);
  }
}

// Initial load
loadProducts();
loadTags();
</script>

<style scoped>
.hoverable-rows :deep(tbody tr) {
  transition: background-color 0.2s ease;
  cursor: pointer;
}

.hoverable-rows :deep(tbody tr:hover) {
  background-color: var(--p-surface-100);
}
</style>

<style>
/* External product drawer - positioned on the right */
.external-product-drawer {
  width: 450px !important;
}

/* When both drawers are open, shift external drawer */
.external-product-drawer.drawer-with-phottr {
  right: 0 !important;
}

/* Phottr product drawer - positioned next to external drawer */
.phottr-product-drawer {
  width: 500px !important;
  right: 450px !important;
  border-left: 1px solid var(--p-surface-200) !important;
}

/* Ensure Phottr drawer doesn't have its own mask overlay */
.phottr-product-drawer .p-drawer-mask {
  background: transparent !important;
}

/* Horizontal scroll for versions */
.versions-scroll {
  scrollbar-width: thin;
  scrollbar-color: var(--p-surface-300) transparent;
}

.versions-scroll::-webkit-scrollbar {
  height: 6px;
}

.versions-scroll::-webkit-scrollbar-track {
  background: transparent;
}

.versions-scroll::-webkit-scrollbar-thumb {
  background-color: var(--p-surface-300);
  border-radius: 3px;
}

.versions-scroll::-webkit-scrollbar-thumb:hover {
  background-color: var(--p-surface-400);
}
</style>
